import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { DeleteConfirmationDialog } from "@/components/ui/delete-confirmation-dialog"
import { useToast } from "@/hooks/useToast"
import { ColumnDef } from "@tanstack/react-table"
import { Table } from "@/components/ui/table"
import {
  useListRoles,
  useListInvitations,
  useListMembers,
  useInviteTeamMember,
  useResendInvitation,
  useCancelInvitation,
  useRemoveTeamMember,
} from "@/hooks/useTeam"
import { useQueryClient } from "@tanstack/react-query"
import { Invitation, TeamMember } from "@/types/api"
import { useActiveOrganization } from "@/hooks/useOrganization"
import { useAuth } from "@/hooks/useAuth"
import { UserPlus, CheckCircle, MoreVertical, RotateCcw, X } from "lucide-react"
import { CopyButton } from "@/components/ui/copy"

export default function TeamMembersList() {
  const queryClient = useQueryClient()
  const [inviteModalOpen, setInviteModalOpen] = useState(false)
  const [successModalOpen, setSuccessModalOpen] = useState(false)
  const [inviteUrl, setInviteUrl] = useState("")
  const [email, setEmail] = useState("")
  const [selectedRole, setSelectedRole] = useState("")
  const [activeTab, setActiveTab] = useState<"team" | "invitations">("team")
  const [inviteError, setInviteError] = useState("")
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false)
  const [selectedInvitation, setSelectedInvitation] = useState<Invitation | null>(null)
  const [removeMemberDialogOpen, setRemoveMemberDialogOpen] = useState(false)
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null)
  const { toast } = useToast()

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  const handleOpenInviteModal = () => {
    setInviteError("")
    setEmail("")
    setSelectedRole("")
    setInviteModalOpen(true)
  }

  const { data: roles, isLoading: rolesLoading } = useListRoles()
  const { data: invitations, isLoading: invitationsLoading } = useListInvitations()
  const { data: members, isLoading: membersLoading } = useListMembers()
  const activeOrganization = useActiveOrganization()
  const inviteTeamMemberMutation = useInviteTeamMember()
  const resendInvitationMutation = useResendInvitation()
  const cancelInvitationMutation = useCancelInvitation()
  const removeTeamMemberMutation = useRemoveTeamMember()

  // Get current user's role and info
  const currentUserRole = activeOrganization?.role?.name || ""
  const isOwner = currentUserRole === "owner"

  // Get current user info from auth store
  const { user } = useAuth()

  const handleInvite = () => {
    if (!email || !selectedRole || inviteTeamMemberMutation.isPending) return

    // Clear any previous errors
    setInviteError("")

    inviteTeamMemberMutation.mutate(
      { email, role: selectedRole },
      {
        onSuccess: (response) => {
          queryClient.invalidateQueries({ queryKey: ["team", "invitations"] })
          queryClient.invalidateQueries({ queryKey: ["team", "members"] })
          setInviteUrl(response.invite_url)
          setInviteModalOpen(false)
          setSuccessModalOpen(true)
          setEmail("")
          setSelectedRole("")
        },
        onError: (error: any) => {
          // Handle specific error messages
          if (error?.response?.data?.error?.includes("invite to this email already exists")) {
            setInviteError("An invitation has already been sent to this email address.")
          } else {
            setInviteError(error?.response?.data?.error || "Failed to send invitation. Please try again.")
          }
        },
      }
    )
  }

  const handleResendInvitation = (invitation: Invitation) => {
    resendInvitationMutation.mutate(invitation.email, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["team", "invitations"] })
        toast({
          title: "Invitation Resent",
          description: `Invitation has been resent to ${invitation.email}`,
        })
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.response?.data?.error || "Failed to resend invitation",
          variant: "destructive",
        })
      },
    })
  }

  const handleCancelInvitation = (invitation: Invitation) => {
    setSelectedInvitation(invitation)
    setCancelDialogOpen(true)
  }

  const confirmCancelInvitation = () => {
    if (!selectedInvitation) return

    cancelInvitationMutation.mutate(selectedInvitation.email, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["team", "invitations"] })
        setCancelDialogOpen(false)
        setSelectedInvitation(null)
        toast({
          title: "Invitation Cancelled",
          description: `Invitation for ${selectedInvitation.email} has been cancelled`,
        })
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.response?.data?.error || "Failed to cancel invitation",
          variant: "destructive",
        })
      },
    })
  }

  const handleRemoveMember = (member: TeamMember) => {
    setSelectedMember(member)
    setRemoveMemberDialogOpen(true)
  }

  const confirmRemoveMember = () => {
    if (!selectedMember) return

    removeTeamMemberMutation.mutate(selectedMember.id, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["team", "members"] })
        setRemoveMemberDialogOpen(false)
        setSelectedMember(null)
        toast({
          title: "Member Removed",
          description: `${selectedMember.first_name} ${selectedMember.last_name} has been removed from the team`,
        })
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.response?.data?.error || "Failed to remove team member",
          variant: "destructive",
        })
      },
    })
  }

  const memberColumns: ColumnDef<TeamMember>[] = [
    {
      accessorKey: "email",
      header: "Email",
      cell: (info: any) => (
        <div className="flex items-center gap-2">
          <span>{info.getValue() as string}</span>
        </div>
      ),
    },
    {
      accessorKey: "first_name",
      header: "Name",
      cell: (info: any) => {
        const firstName = info.getValue() as string
        const lastName = info.row.original.last_name
        return (
          <div className="flex items-center gap-2">
            <span>
              {firstName} {lastName}
            </span>
          </div>
        )
      },
    },
    {
      accessorKey: "role",
      header: "Role",
      cell: (info: any) => (
        <div className="flex items-center gap-2">
          <span className="capitalize">{(info.getValue() as string) || "No role"}</span>
        </div>
      ),
    },
    {
      accessorKey: "joined_at",
      header: "Joined",
      cell: (info: any) => (
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600 dark:text-gray-400">{formatDate(info.getValue() as string)}</span>
        </div>
      ),
    },
    {
      id: "actions",
      header: "Actions",
      cell: (info: any) => {
        const member = info.row.original as TeamMember
        const memberIsOwner = member.role === "owner"
        const isCurrentUser = user?.id === member.id
        const canRemoveMember = isOwner && !memberIsOwner && !isCurrentUser

        return (
          <div className="flex items-center justify-start">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                if (canRemoveMember && !removeTeamMemberMutation.isPending) {
                  handleRemoveMember(member)
                }
              }}
              className={`h-8 px-2 ${
                canRemoveMember
                  ? "text-red-600 hover:bg-red-50 hover:text-red-700 dark:hover:bg-red-900/20"
                  : "cursor-not-allowed text-gray-400 hover:bg-transparent hover:text-gray-400 dark:text-gray-600"
              }`}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        )
      },
    },
  ]

  const invitationColumns: ColumnDef<Invitation>[] = [
    {
      accessorKey: "email",
      header: "Email",
      cell: (info: any) => (
        <div className="flex items-center gap-2">
          <span>{info.getValue() as string}</span>
        </div>
      ),
    },
    {
      accessorKey: "role",
      header: "Role",
      cell: (info: any) => (
        <div className="flex items-center gap-2">
          <span className="capitalize">{info.getValue() as string}</span>
        </div>
      ),
    },
    {
      accessorKey: "created_at",
      header: "Sent",
      cell: (info: any) => (
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600 dark:text-gray-400">{formatDate(info.getValue() as string)}</span>
        </div>
      ),
    },
    {
      accessorKey: "expires_at",
      header: "Expires",
      cell: (info: any) => {
        const expiresAt = new Date(info.getValue() as string)
        const now = new Date()
        const isExpired = expiresAt < now
        const isExpiringSoon = expiresAt < new Date(now.getTime() + 2 * 60 * 60 * 1000) // 2 hours

        return (
          <div className="flex items-center gap-2">
            <span
              className={`text-sm ${isExpired ? "text-red-600" : isExpiringSoon ? "text-orange-600" : "text-gray-600 dark:text-gray-400"}`}
            >
              {formatDate(info.getValue() as string)}
            </span>
          </div>
        )
      },
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: (info: any) => {
        const expiresAt = new Date(info.row.original.expires_at)
        const now = new Date()
        const isExpired = expiresAt < now

        return (
          <div className="flex items-center">
            <span
              className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                isExpired
                  ? "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
                  : "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200"
              }`}
            >
              {isExpired ? "Expired" : "Pending"}
            </span>
          </div>
        )
      },
    },
    {
      id: "actions",
      header: "Actions",
      cell: (info: any) => {
        const invitation = info.row.original as Invitation
        const expiresAt = new Date(invitation.expires_at)
        const now = new Date()
        const isExpired = expiresAt < now

        return (
          <div className="flex items-center justify-end">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0" disabled={isExpired}>
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={() => handleResendInvitation(invitation)}
                  disabled={resendInvitationMutation.isPending}
                >
                  <RotateCcw className="mr-2 h-4 w-4" />
                  Resend
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => handleCancelInvitation(invitation)}
                  disabled={cancelInvitationMutation.isPending}
                  className="text-red-600 focus:text-red-600"
                >
                  <X className="mr-2 h-4 w-4" />
                  Cancel
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )
      },
    },
  ]

  return (
    <>
      <div className="space-y-6 px-4">
        {/* Header with Tabs */}
        <div className="flex items-center justify-between">
          <div className="flex space-x-8 border-b border-gray-200 dark:border-gray-700">
            <button
              onClick={() => setActiveTab("team")}
              className={`pb-2 text-sm font-medium transition-colors ${
                activeTab === "team"
                  ? "border-b-2 border-gray-900 text-gray-900 dark:border-white dark:text-white"
                  : "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
              }`}
            >
              Members
            </button>
            <button
              onClick={() => setActiveTab("invitations")}
              className={`pb-2 text-sm font-medium transition-colors ${
                activeTab === "invitations"
                  ? "border-b-2 border-gray-900 text-gray-900 dark:border-white dark:text-white"
                  : "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
              }`}
            >
              Invitations
            </button>
          </div>
          <Button size="xs" onClick={handleOpenInviteModal}>
            <UserPlus className="mr-2 h-4 w-4" />
            Invite Member
          </Button>
        </div>

        {/* Tab Content */}
        <div className="space-y-4">
          {activeTab === "team" && (
            <>
              <Table
                data={members || []}
                columns={memberColumns}
                isLoading={membersLoading}
                emptyMessage="No team members found."
                className="divide-y divide-gray-200 dark:divide-gray-700"
                headerClassName="bg-gray-50 dark:bg-[#18181b]"
                bodyClassName="bg-white dark:bg-[#0F0F12] divide-y divide-gray-100 dark:divide-gray-800"
                cellClassName="py-2 text-sm"
              />
            </>
          )}

          {activeTab === "invitations" && (
            <>
              <Table
                data={invitations || []}
                columns={invitationColumns}
                isLoading={invitationsLoading}
                emptyMessage="No pending invitations found."
                className="divide-y divide-gray-200 dark:divide-gray-700"
                headerClassName="bg-gray-50 dark:bg-[#18181b]"
                bodyClassName="bg-white dark:bg-[#0F0F12] divide-y divide-gray-100 dark:divide-gray-800"
                cellClassName="py-2 text-sm"
              />
            </>
          )}
        </div>
      </div>

      {/* Invite Dialog */}
      <Dialog open={inviteModalOpen} onOpenChange={setInviteModalOpen}>
        <DialogContent className="max-w-xl overflow-visible p-0">
          <div className="flex items-start justify-between px-6 pt-6">
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-600 dark:bg-gray-700">
              <UserPlus className="h-6 w-6 text-gray-600 dark:text-gray-300" />
            </div>
          </div>
          <DialogHeader className="px-6 pt-1">
            <DialogTitle className="text-2xl font-semibold">Invite Team Member</DialogTitle>
            <DialogDescription className="mt-2 text-sm">
              Send an invitation to join your team with a specific role.
            </DialogDescription>
          </DialogHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault()
              handleInvite()
            }}
          >
            <div className="mt-4 space-y-6 px-6">
              {inviteError && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-600 dark:bg-red-700">
                  <div className="flex items-center gap-2">
                    <div className="h-4 w-4 rounded-full bg-red-500"></div>
                    <span className="text-sm text-red-700 dark:text-red-300">{inviteError}</span>
                  </div>
                </div>
              )}
              <Input
                label="Email Address"
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value)
                  if (inviteError) setInviteError("")
                }}
                placeholder="Enter email address"
                required
              />
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Role</label>
                <Select
                  value={selectedRole}
                  onValueChange={(value) => {
                    setSelectedRole(value)
                    if (inviteError) setInviteError("")
                  }}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a role">
                      <span className="capitalize">{selectedRole}</span>
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {rolesLoading ? (
                      <SelectItem value="" disabled>
                        Loading roles...
                      </SelectItem>
                    ) : (
                      roles?.map((role) => (
                        <SelectItem key={role.id} value={role.name}>
                          <div className="flex flex-col">
                            <span className="font-medium capitalize">{role.name}</span>
                            <span className="text-xs text-gray-500">{role.description}</span>
                          </div>
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center justify-end gap-6 px-6 pb-5 pt-2">
              <Button
                variant="ghost"
                type="button"
                onClick={() => setInviteModalOpen(false)}
                disabled={inviteTeamMemberMutation.isPending}
                size="xs"
                className="text-primary-600 bg-transparent px-3 font-normal shadow-none hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={!email || !selectedRole || inviteTeamMemberMutation.isPending}
                className="min-w-[80px]"
              >
                {inviteTeamMemberMutation.isPending ? "Inviting..." : "Send Invite"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Success Modal */}
      <Dialog open={successModalOpen} onOpenChange={setSuccessModalOpen}>
        <DialogContent className="max-w-xl overflow-visible p-0">
          <div className="flex items-start justify-between px-6 pt-6">
            <div className="rounded-lg border border-green-200 bg-green-50 p-3 dark:border-green-600 dark:bg-green-700">
              <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-300" />
            </div>
          </div>
          <DialogHeader className="px-6 pt-1">
            <DialogTitle className="text-2xl font-semibold text-green-900 dark:text-green-100">
              Invitation Sent!
            </DialogTitle>
            <DialogDescription className="mt-2 text-sm text-green-700 dark:text-green-300">
              The invitation has been sent successfully. You can share the invitation link below with the team member.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 space-y-6 px-6">
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Invitation Link</label>
              <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-600 dark:bg-gray-700">
                <input
                  type="text"
                  value={inviteUrl}
                  readOnly
                  className="flex-1 bg-transparent text-sm text-gray-900 focus:outline-none dark:text-gray-100"
                />
                <CopyButton
                  text={inviteUrl}
                  size="xs"
                  variant="ghost"
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                />
              </div>
            </div>
          </div>
          <div className="flex items-center justify-end gap-6 px-6 pb-5 pt-2">
            <Button
              variant="ghost"
              type="button"
              onClick={() => setSuccessModalOpen(false)}
              size="xs"
              className="text-primary-600 bg-transparent px-3 font-normal shadow-none hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              Close
            </Button>
            <Button
              onClick={() => {
                setSuccessModalOpen(false)
                setActiveTab("invitations")
              }}
              className="min-w-[80px]"
            >
              View Invitations
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cancel Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={cancelDialogOpen}
        onOpenChange={setCancelDialogOpen}
        title="Cancel Invitation"
        description="Are you sure you want to cancel the invitation for {itemName}? This action cannot be undone and the invitation will be permanently removed."
        itemName={selectedInvitation?.email || ""}
        onConfirm={confirmCancelInvitation}
        isDeleting={cancelInvitationMutation.isPending}
        confirmButtonText="Cancel Invitation"
        cancelButtonText="Keep Invitation"
      />

      {/* Remove Member Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={removeMemberDialogOpen}
        onOpenChange={setRemoveMemberDialogOpen}
        title="Remove Team Member"
        description="Are you sure you want to remove {itemName} from the team? This action cannot be undone and they will lose access to the organization."
        itemName={selectedMember ? `${selectedMember.first_name} ${selectedMember.last_name}` : ""}
        onConfirm={confirmRemoveMember}
        isDeleting={removeTeamMemberMutation.isPending}
        confirmButtonText="Remove Member"
        cancelButtonText="Cancel"
      />
    </>
  )
}
